import type { ComponentType } from 'react'
import {
  Book,
  Boxes,
  Folder,
  Gamepad2,
  Globe,
  GraduationCap,
  Heart,
  Laptop,
  Paintbrush,
  Flag,
  FileText,
  Smartphone,
  ShoppingCart,
  Star,
  Wrench,
  Zap,
} from 'lucide-react'

import { cn } from '@/features/issue'

/**
 * The project icon and colour pickers.
 *
 * The stored icon is an SF SYMBOL NAME, because the iPad app renders it with SF
 * Symbols natively. The web has no SF Symbols, so typing a name into a text box
 * was a guessing game with no feedback: a typo, or any symbol the iPad does not
 * offer, silently produced a blank tile there and nothing here.
 *
 * So each SF Symbol is paired with the closest icon from lucide-react — a real
 * icon set, not hand-written path data — and the picker offers exactly the
 * sixteen the iPad app itself offers (Taskly/Models/ProjectModel.swift).
 * Whatever is chosen here renders on both surfaces.
 */

export type ProjectIconOption = {
  /** The SF Symbol name. This is what gets stored. */
  name: string
  label: string
  Icon: ComponentType<{ className?: string; strokeWidth?: number }>
}

export const PROJECT_ICONS: ProjectIconOption[] = [
  { name: 'folder.fill', label: 'Folder', Icon: Folder },
  { name: 'doc.text.fill', label: 'Document', Icon: FileText },
  { name: 'flag.fill', label: 'Flag', Icon: Flag },
  { name: 'star.fill', label: 'Star', Icon: Star },
  { name: 'heart.fill', label: 'Heart', Icon: Heart },
  { name: 'bolt.fill', label: 'Bolt', Icon: Zap },
  { name: 'cube.fill', label: 'Cube', Icon: Boxes },
  { name: 'cart.fill', label: 'Cart', Icon: ShoppingCart },
  { name: 'book.fill', label: 'Book', Icon: Book },
  { name: 'graduationcap.fill', label: 'Graduation', Icon: GraduationCap },
  { name: 'paintbrush.fill', label: 'Paintbrush', Icon: Paintbrush },
  { name: 'wrench.and.screwdriver.fill', label: 'Tools', Icon: Wrench },
  { name: 'desktopcomputer', label: 'Desktop', Icon: Laptop },
  { name: 'iphone', label: 'iPhone', Icon: Smartphone },
  { name: 'gamecontroller.fill', label: 'Controller', Icon: Gamepad2 },
  { name: 'globe', label: 'Globe', Icon: Globe },
]

/** The colour names the iPad app knows, in the order it shows them. */
export const PROJECT_COLORS = [
  'blue',
  'indigo',
  'purple',
  'pink',
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'gray',
] as const

export type ProjectColor = (typeof PROJECT_COLORS)[number]

/**
 * What each colour name looks like on iOS. The database stores the NAME; this
 * exists only so the picker previews the tile the way the iPad will draw it.
 */
export const COLOR_HEX: Record<string, string> = {
  blue: '#0A84FF',
  indigo: '#5E5CE6',
  purple: '#BF5AF2',
  pink: '#FF375F',
  red: '#FF453A',
  orange: '#FF9F0A',
  yellow: '#E6A700',
  green: '#2FA84F',
  teal: '#009DB5',
  gray: '#7C7C82',
}

export function colorHex(name: string | null | undefined): string {
  return COLOR_HEX[(name ?? '').toLowerCase()] ?? COLOR_HEX.blue!
}

export function iconOptionFor(icon: string | null | undefined): ProjectIconOption {
  return PROJECT_ICONS.find((option) => option.name === icon) ?? PROJECT_ICONS[0]!
}

/** The tinted rounded square that represents a project. */
export function ProjectIconTile({
  icon,
  color,
  size = 'md',
  className,
  logoUrl,
}: {
  icon: string
  color: string
  size?: 'xs' | 'sm' | 'md'
  className?: string
  /**
   * An uploaded logo, which replaces the symbol when present.
   *
   * The symbol is kept in the database either way rather than cleared: it is
   * what the iPad renders, since SF Symbols are drawn natively there and it has
   * no concept of an uploaded file. Removing the logo therefore falls straight
   * back to the symbol instead of leaving a project with no mark at all.
   */
  logoUrl?: string | null
}) {
  const { Icon } = iconOptionFor(icon)
  const hex = colorHex(color)
  const box = size === 'xs' ? 'size-5 rounded-md' : size === 'sm' ? 'size-7 rounded-lg' : 'size-9 rounded-lg'

  if (logoUrl) {
    return (
      <span className={cn('inline-flex shrink-0 overflow-hidden bg-ink-100 dark:bg-ink-800', box, className)}>
        {/* object-cover, so a wide or tall logo fills the square instead of
            letterboxing into it. The alt is empty because the project name is
            always beside it — announcing it twice is noise to a screen
            reader. */}
        <img src={logoUrl} alt="" className="size-full object-cover" loading="lazy" />
      </span>
    )
  }

  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center', box, className)}
      style={{ backgroundColor: `${hex}1F`, color: hex }}
      aria-hidden="true"
    >
      <Icon
        className={size === 'xs' ? 'size-3' : size === 'sm' ? 'size-4' : 'size-5'}
        strokeWidth={size === 'xs' ? 2 : 1.8}
      />
    </span>
  )
}

export function IconPicker({
  value,
  color,
  disabled,
  onChange,
}: {
  value: string
  color: string
  disabled?: boolean
  onChange: (icon: string) => void
}) {
  const selected = PROJECT_ICONS.some((option) => option.name === value) ? value : ''
  return (
    <div>
      <div role="radiogroup" aria-label="Project icon" className="grid grid-cols-8 gap-1.5">
        {PROJECT_ICONS.map((option) => {
          const isSelected = option.name === selected
          return (
            <button
              key={option.name}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${option.label} — ${option.name}`}
              title={option.name}
              disabled={disabled}
              onClick={() => onChange(option.name)}
              className={cn(
                'flex items-center justify-center rounded-lg border p-1.5 transition',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                'disabled:cursor-not-allowed disabled:opacity-50',
                isSelected
                  ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                  : 'border-ink-200 bg-white hover:border-ink-300 hover:bg-ink-50',
              )}
            >
              <ProjectIconTile icon={option.name} color={color} size="sm" />
            </button>
          )
        })}
      </div>
      <p className="mt-1.5 text-xs font-normal text-ink-500">
        {selected ? (
          <>
            Stored as <code className="rounded bg-ink-100 px-1 py-0.5">{selected}</code> — an SF
            Symbol, so the iPad app draws the same icon.
          </>
        ) : value ? (
          <>
            <code className="rounded bg-ink-100 px-1 py-0.5">{value}</code> is not one of the
            sixteen the iPad app offers, so it shows as blank there. Pick one above to fix it.
          </>
        ) : (
          <>Pick an icon. The iPad app draws the same one.</>
        )}
      </p>
    </div>
  )
}

export function ColorPicker({
  value,
  disabled,
  onChange,
}: {
  value: string
  disabled?: boolean
  onChange: (color: string) => void
}) {
  return (
    <div role="radiogroup" aria-label="Project colour" className="flex flex-wrap gap-2">
      {PROJECT_COLORS.map((name) => {
        const isSelected = name === value
        return (
          <button
            key={name}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={name}
            title={name}
            disabled={disabled}
            onClick={() => onChange(name)}
            className={cn(
              'size-7 rounded-full transition',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
              'disabled:cursor-not-allowed disabled:opacity-50',
              isSelected
                ? 'ring-2 ring-ink-900 ring-offset-2'
                : 'ring-1 ring-ink-200 hover:ring-ink-400',
            )}
            style={{ backgroundColor: colorHex(name) }}
          />
        )
      })}
    </div>
  )
}
